import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PremiumBackButton from "../components/PremiumBackButton";
import BookPreviewModal from "../components/BookPreviewModal";
import AuthModal from "../components/AuthModal";
import { useAuth } from "../context/AuthContext";
import KidBackground from "../ui/backgrounds/KidBackground";
import splashImage from '../assets/splash.png';
import { Home, Palette, LogOut, LogIn, UserPlus } from 'lucide-react';
import { createPageBooks as books } from '../data/artmosphereBooks';

export default function CreateBooksPage() {
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [previewBook, setPreviewBook] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAccountDropdown && !event.target.closest('.account-dropdown')) {
        setShowAccountDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAccountDropdown]);

  // Listen for global auth modal events
  useEffect(() => {
    const handleAuthModalEvent = (event) => {
      if (event.detail && event.detail.mode) {
        setAuthMode(event.detail.mode);
        setShowAuthModal(true);
      }
    };

    window.addEventListener('open_auth_modal', handleAuthModalEvent);
    return () => {
      window.removeEventListener('open_auth_modal', handleAuthModalEvent);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handlePreview = (book) => {
    setPreviewBook(book);
    setShowPreview(true);
  };

  const handleAddToCartFromPreview = (book) => {
    toggleBook(book);
  };

  const toggleBook = (book) => {
    setSelectedBooks((prev) =>
      prev.find((b) => b.id === book.id)
        ? prev.filter((b) => b.id !== book.id)
        : [...prev, { ...book, quantity: 1 }]
    );
  };

  const updateQuantity = (bookId, newQuantity) => {
    if (newQuantity < 1) return;
    setSelectedBooks((prev) =>
      prev.map((book) =>
        book.id === bookId ? { ...book, quantity: newQuantity } : book
      )
    );
  };

  const increaseQuantity = (bookId) => {
    updateQuantity(bookId, (selectedBooks.find(b => b.id === bookId)?.quantity || 0) + 1);
  };

  const decreaseQuantity = (bookId) => {
    const currentQuantity = selectedBooks.find(b => b.id === bookId)?.quantity || 1;
    if (currentQuantity > 1) {
      updateQuantity(bookId, currentQuantity - 1);
    }
  };

  const addToCart = (book) => {
    const existingBook = selectedBooks.find(b => b.id === book.id);
    if (existingBook) {
      increaseQuantity(book.id);
    } else {
      toggleBook(book);
    }
  };

  const totalAmount = selectedBooks.reduce((sum, book) => sum + (book.price * (book.quantity || 1)), 0);

  return (
    <div className="artm-page artm-page-pad min-h-screen h-screen flex flex-col relative bg-[#FFF8E1] px-0 py-0 md:px-[2.5cm] md:py-6">
      {/* Amber color splashes background - matching Art History / home */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-amber-200/25 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 left-10 w-96 h-96 bg-amber-300/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-1/4 w-56 h-56 bg-amber-400/22 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-1/3 w-40 h-40 bg-amber-200/15 rounded-full blur-2xl"></div>
        <div className="absolute bottom-1/4 right-1/3 w-48 h-48 bg-amber-300/18 rounded-full blur-3xl"></div>
      </div>
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-5">
        <img src={splashImage} alt="" className="absolute top-10 right-5 opacity-20" style={{ width: '400px', height: '200px', objectFit: 'cover', transform: 'rotate(15deg)', mixBlendMode: 'multiply', filter: 'blur(2px)' }} />
        <img src={splashImage} alt="" className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-15" style={{ width: '300px', height: '150px', objectFit: 'cover', transform: 'translate(-50%, -50%) rotate(10deg)', mixBlendMode: 'multiply', filter: 'blur(3px)' }} />
      </div>
      <KidBackground variant="blobs" intensity={1} />

      {/* Art History–style window: single white card with sleek border */}
      <div className="relative z-10 w-full max-w-full flex-1 min-h-0 flex flex-col py-0 md:py-4 flex-grow">
        <div className="bg-white rounded-none md:rounded-[32px] shadow-2xl border-0 md:border-2 border-amber-300 flex flex-col flex-1 min-h-0 overflow-hidden min-h-[50vh]">
          {/* Top bar: Back left, nav pills right */}
          <header className="flex-shrink-0 bg-white/95 backdrop-blur-sm border-b border-amber-200/50 shadow-sm">
            <div className="flex justify-between items-center py-2 md:py-2.5 px-3 md:px-6 min-h-[52px] gap-2">
              <PremiumBackButton to={-1} label="Back" />
            <div className="flex items-center gap-1.5 md:gap-2 relative z-[100]" style={{ pointerEvents: 'auto' }}>
              <button
                onClick={() => navigate('/home')}
                className="p-1 md:px-4 md:py-2 art-btn-nav-pill text-[7px] md:text-sm font-medium rounded-full flex items-center justify-center relative z-[101] cursor-pointer"
                style={{ pointerEvents: 'auto', position: 'relative' }}
                title="Home"
                type="button"
              >
                <Home className="w-2 h-2 md:w-4 md:h-4 flex-shrink-0 text-amber-800 pointer-events-none" />
                <span className="hidden md:inline ml-1 text-amber-800 pointer-events-none">Home</span>
              </button>
              <button
                onClick={() => navigate('/my-art-world')}
                className="p-1 md:px-4 md:py-2 art-btn-nav-pill text-[7px] md:text-sm font-medium rounded-full flex items-center justify-center relative z-[101] cursor-pointer"
                style={{ pointerEvents: 'auto', position: 'relative' }}
                title="Artist Corner"
                type="button"
              >
                <Palette className="w-2 h-2 md:w-4 md:h-4 flex-shrink-0 text-amber-800 pointer-events-none" />
                <span className="hidden md:inline ml-1 text-amber-800 pointer-events-none">Artist Corner</span>
              </button>
              {isAuthenticated ? (
                <button
                  onClick={handleSignOut}
                  className="p-1 md:px-4 md:py-2 art-btn-nav-pill text-[7px] md:text-sm font-medium rounded-full flex items-center justify-center"
                  title="Sign Out"
                  type="button"
                >
                  <LogOut className="w-2 h-2 md:w-4 md:h-4 flex-shrink-0 text-amber-800" />
                  <span className="hidden md:inline ml-1 text-amber-800">Sign Out</span>
                </button>
              ) : (
                // Not authenticated: show Login/Join
                <div className="flex items-center gap-1.5 md:gap-2">
                  <button
                    onClick={() => {
                      console.log('Login button clicked');
                      setAuthMode('login');
                      setShowAuthModal(true);
                    }}
                    className="p-1 md:px-4 md:py-2 art-btn-nav-pill text-[7px] md:text-sm font-medium rounded-full flex items-center justify-center relative z-[101] cursor-pointer"
                    style={{ pointerEvents: 'auto', position: 'relative' }}
                    title="Login"
                    type="button"
                  >
                    <LogIn className="w-2 h-2 md:w-4 md:h-4 flex-shrink-0 text-amber-800 pointer-events-none" />
                    <span className="hidden md:inline ml-1 text-amber-800 pointer-events-none">Login</span>
                  </button>
                  <button
                    onClick={() => {
                      console.log('Join button clicked');
                      setAuthMode('signup');
                      setShowAuthModal(true);
                    }}
                    className="p-1 md:px-4 md:py-2 art-btn-primary text-[7px] md:text-sm font-medium rounded-full shadow-sm hover:from-amber-600 hover:to-amber-700 transition-all flex items-center justify-center relative z-[101] cursor-pointer"
                    style={{ pointerEvents: 'auto', position: 'relative' }}
                    title="Join Community"
                    type="button"
                  >
                    <UserPlus className="w-2 h-2 md:w-4 md:h-4 flex-shrink-0 text-white pointer-events-none" />
                    <span className="hidden md:inline ml-1 text-white pointer-events-none">Join</span>
                  </button>
                </div>
              )}
            </div>
            </div>
          </header>

          {/* Centered page title and tagline */}
          <div className="flex-shrink-0 text-center py-6 md:py-8 px-4 border-b border-amber-100/80">
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-3 tracking-tight">Artmosphere Book Collection</h2>
            <p className="text-slate-700 text-lg md:text-xl">Pick your favorites and start creating!</p>
          </div>

          {/* Scrollable content - book grid */}
          <div className="flex-1 min-h-0 overflow-auto max-w-7xl mx-auto w-full p-4 md:p-6 pb-12">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {books.map((book) => (
                <div
                  key={book.id}
                  className={`cursor-pointer rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 hover:scale-[1.02] bg-white relative overflow-hidden border-2 ${
                    selectedBooks.find(b => b.id === book.id) 
                      ? 'border-amber-400 ring-2 ring-amber-300 shadow-amber-200' 
                      : 'border-amber-100'
                  }`}
                  style={{ height: '400px' }}
                  onClick={() => toggleBook(book)}
                >
                  {/* Cart Indicator */}
                  {selectedBooks.find(b => b.id === book.id) && (
                    <div className="absolute top-3 right-3 bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg animate-pulse z-10">
                      {selectedBooks.find(b => b.id === book.id)?.quantity || 1}
                    </div>
                  )}
                  {/* Image Section - 45% of card */}
                  <div className="w-full bg-gray-100 flex items-center justify-center overflow-hidden" style={{ height: '45%' }}>
                    <img
                      src={book.image}
                      alt={book.title}
                      className="w-full h-full object-cover object-top"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/400x200.png?text=Image+Not+Found";
                      }}
                    />
                  </div>
                  {/* Content Section - 55% of card */}
                  <div className="p-4 flex flex-col justify-between" style={{ height: '55%', backgroundColor: '#FFF8E1' }}>
                    <div>
                      <h3 className="text-base font-bold text-slate-900 mb-2 leading-tight">{book.title}</h3>
                      <p className="text-xs text-slate-700 mb-3 line-clamp-2">{book.description}</p>
                    </div>
                  <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-slate-900">₹{book.price}</span>
                    
                    {/* Preview and Cart Controls */}
                      <div className="flex items-center gap-2 flex-wrap">
                      {/* Preview Button */}
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePreview(book); }}
                          className="px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 transition-all duration-200 flex items-center gap-1"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                        </svg>
                        Preview
                      </button>
                      
                      {/* Sophisticated Quantity Controls */}
                      {selectedBooks.find(b => b.id === book.id) ? (
                          <div className="flex items-center gap-2">
                          {/* Quantity Display */}
                            <div className="flex items-center gap-1.5 bg-white rounded-full px-2 py-0.5 border border-amber-200 shadow-sm">
                            <button
                              onClick={(e) => { e.stopPropagation(); decreaseQuantity(book.id); }}
                                className="w-5 h-5 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-700 flex items-center justify-center text-xs font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={selectedBooks.find(b => b.id === book.id)?.quantity <= 1}
                            >
                              −
                            </button>
                              <span className="w-6 text-center text-xs font-semibold text-slate-900">
                              {selectedBooks.find(b => b.id === book.id)?.quantity || 1}
                            </span>
                            <button
                              onClick={(e) => { e.stopPropagation(); increaseQuantity(book.id); }}
                                className="w-5 h-5 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-700 flex items-center justify-center text-xs font-semibold transition-all duration-200"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); addToCart(book); }}
                            className="art-btn-primary px-3 py-1.5 rounded-lg text-xs font-medium shadow-md transition-all duration-300"
                        >
                          Add to Cart
                        </button>
                      )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* Bottom Cart Summary */}
          {selectedBooks.length > 0 && (
            <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-xl border-t border-amber-200 p-4 z-50">
              <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                <div className="md:col-span-2 max-h-40 overflow-y-auto">
                  {selectedBooks.map((b) => (
                    <div key={b.id} className="flex items-center justify-between py-3 border-b border-amber-100 last:border-0">
                      <div className="flex items-center gap-3">
                        <img src={b.image} alt={b.title} className="w-12 h-12 rounded-lg object-cover border border-amber-200 shadow-sm" />
                        <div className="flex flex-col">
                          <div className="text-sm font-semibold text-slate-900">{b.title}</div>
                          <div className="text-xs text-slate-600">₹{b.price} each</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {/* Quantity Controls in Cart */}
                        <div className="flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 border border-amber-200 shadow-sm">
                          <button
                            onClick={() => decreaseQuantity(b.id)}
                            className="w-5 h-5 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-700 flex items-center justify-center text-xs font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={b.quantity <= 1}
                          >
                            −
                          </button>
                          <span className="w-6 text-center text-xs font-semibold text-slate-900">
                            {b.quantity || 1}
                          </span>
                          <button
                            onClick={() => increaseQuantity(b.id)}
                            className="w-5 h-5 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-700 flex items-center justify-center text-xs font-semibold transition-all duration-200"
                          >
                            +
                          </button>
                        </div>
                        
                        <div className="text-right">
                          <div className="text-sm font-semibold text-slate-900">₹{(b.price * (b.quantity || 1)).toLocaleString()}</div>
                          <div className="text-xs text-slate-500">Qty: {b.quantity || 1}</div>
                        </div>
                        
                        <button 
                          onClick={() => setSelectedBooks((prev) => prev.filter((x) => x.id !== b.id))} 
                          className="text-xs px-2 py-1 rounded-full border border-red-200 text-red-700 hover:bg-red-50 transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="md:col-span-1 flex items-center md:justify-end justify-between gap-4">
                  <div>
                    <p className="text-slate-600 text-sm">
                      Items: {selectedBooks.reduce((sum, book) => sum + (book.quantity || 1), 0)}
                    </p>
                    <p className="text-xs text-slate-500 mb-1">
                      {selectedBooks.length} unique products
                    </p>
                    <p className="text-xl font-bold text-slate-900">Total: ₹{totalAmount.toLocaleString()}</p>
                  </div>
                  <button 
                    onClick={() => navigate('/checkout', { state: { items: selectedBooks } })} 
                    className="art-btn-primary px-6 py-3 rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    Proceed to Payment
                  </button>
                </div>
              </div>
            </div>
          )}

      {/* Book Preview Modal */}
        <BookPreviewModal
          book={previewBook}
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          onAddToCart={handleAddToCartFromPreview}
        />
        
        {/* Auth Modal */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          mode={authMode}
          onModeSwitch={setAuthMode}
        />
    </div>
  );
}